import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getAuthStatus, logout } from '../utils/auth';

// Create the context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Use ref to track if we're currently checking auth status
  const checkingRef = useRef(false);

  // Check authentication status only on initial mount
  useEffect(() => {
    const checkAuth = async () => {
      // Skip checking if we're already in the process of checking
      if (checkingRef.current) return;
      
      checkingRef.current = true;
      setIsLoading(true);
      try {
        const authStatus = await getAuthStatus();
        
        setIsAuthenticated(authStatus.authenticated);
        setUser(authStatus.authenticated ? authStatus.user : null);
      } catch (err) {
        console.error('Error checking authentication:', err);
        setError('Failed to check authentication status');
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
        checkingRef.current = false;
      }
    };

    // Initial check
    checkAuth();
    
    // Removed periodic auth check that was causing page refreshes
    // and loss of unsaved changes
    
    // No interval to clean up
  }, []);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      setIsAuthenticated(false);
      setUser(null);
      setError(null);
      return true;
    } catch (err) {
      setError('Failed to logout');
      return false;
    }
  };

  // Update auth state
  const updateAuthState = (authStatus) => {
    setIsAuthenticated(authStatus.authenticated);
    setUser(authStatus.user || null);
  };

  const contextValue = {
    isAuthenticated,
    isLoading,
    user,
    error,
    logout: handleLogout,
    updateAuthState,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;